import React from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import MarketingLayout from "@/components/MarketingLayout";
import { logger } from "@/lib/logger";

export default function StoryPublic() {
  const { slug } = useParams();
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    api.get(`/public/story/${slug}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        logger.warn("public story load failed", err);
        setError(err.response?.status === 404 ? "not_found" : "error");
      });
  }, [slug]);

  if (error === "not_found") return <NotFound />;
  if (error) return <MarketingLayout><div className="p-20 text-center text-[#A1A1AA]">Something went wrong.</div></MarketingLayout>;
  if (!data) return <MarketingLayout><div className="p-20 text-center text-[#A1A1AA] tracking-widest uppercase text-sm">Loading…</div></MarketingLayout>;

  const { project, author_name, scenes, characters, storyboard_frames } = data;
  const hero = storyboard_frames?.[0]?.data_url;

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative overflow-hidden sf-noise" data-testid="public-hero">
        {hero && (
          <>
            <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${hero})` }} />
            <div className="absolute inset-0 bg-[#0A0A0A]/75" />
          </>
        )}
        <div className="absolute inset-0 sf-vignette pointer-events-none" />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 pt-24 pb-24">
          <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-6">
            {project.genre} · {project.format} · {project.target_duration} min
          </div>
          <h1 className="font-editorial text-5xl sm:text-7xl leading-[0.95] max-w-4xl" data-testid="public-title">
            {project.title}
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#E4E4E7] leading-relaxed italic">
            {project.logline}
          </p>
          {author_name && (
            <div className="mt-8 text-sm text-[#A1A1AA]">
              by <span className="text-[#FFFFF0] font-editorial">{author_name}</span>
            </div>
          )}
          <div className="mt-8 flex items-baseline gap-6 text-xs uppercase tracking-widest text-[#71717A]">
            <span>{scenes.length} scenes</span>
            <span className="text-[#27272A]">/</span>
            <span>{characters.length} characters</span>
            {project.public_views != null && <><span className="text-[#27272A]">/</span><span>{project.public_views} views</span></>}
          </div>
        </div>
      </section>

      {/* Characters */}
      {characters.length > 0 && (
        <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-20 border-t border-[#1E1E1E]">
          <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-4">Cast</div>
          <h2 className="font-editorial text-4xl mb-10">Who we meet</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {characters.slice(0, 6).map((c) => (
              <div key={c.id} className="bg-[#141414] border border-[#1E1E1E] overflow-hidden" data-testid={`public-char-${c.id}`}>
                <div className="aspect-[3/4] bg-[#0A0A0A] relative">
                  {c.portrait_data_url ? (
                    <img src={c.portrait_data_url} alt={c.name} className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-[#27272A] font-editorial text-4xl">{c.name?.[0]}</div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="font-editorial text-2xl">{c.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{c.role}</div>
                  </div>
                  <div className="text-sm text-[#A1A1AA] leading-relaxed line-clamp-4">
                    {c.description || c.goal || "—"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Visual language */}
      {storyboard_frames.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 border-t border-[#1E1E1E]">
          <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-4">Visual language</div>
          <h2 className="font-editorial text-4xl mb-10">How it looks</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {storyboard_frames.map((f, i) => (
              <div key={`frame-${i}`} className="aspect-video bg-[#0A0A0A] border border-[#1E1E1E] relative overflow-hidden">
                <img src={f.data_url} alt={f.caption} className="absolute inset-0 w-full h-full object-cover" />
                {f.caption && (
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0A0A0A] to-transparent p-4">
                    <div className="text-xs text-[#E4E4E7] italic">{f.caption}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Scenes preview */}
      <section className="max-w-[1000px] mx-auto px-6 lg:px-10 py-20 border-t border-[#1E1E1E]">
        <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-4">Structure</div>
        <h2 className="font-editorial text-4xl mb-10">Opening beats</h2>
        <div className="space-y-3">
          {scenes.slice(0, 12).map((s) => (
            <div key={s.id} className="border-l-2 border-[#D4AF37] pl-6 py-1" data-testid={`public-scene-${s.number}`}>
              <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Scene {String(s.number).padStart(2, "0")}</div>
              <div className="font-mono-editor text-sm text-[#E4E4E7] mt-1">{s.heading}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#1E1E1E]">
        <div className="max-w-[1000px] mx-auto px-6 py-20 text-center">
          <h2 className="font-editorial text-4xl sm:text-5xl mb-4">Loved this story?</h2>
          <p className="text-[#A1A1AA] max-w-lg mx-auto">
            Build your own on StoryFrame AI &mdash; the intelligent workspace for writers, directors and producers.
          </p>
          <Link to="/signup" className="mt-8 inline-block bg-[#D4AF37] text-[#0A0A0A] px-7 py-3.5 text-sm font-medium hover:bg-[#F59E0B] transition-colors" data-testid="public-cta-signup">
            Start creating free
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}

function NotFound() {
  return (
    <MarketingLayout>
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-3">404</div>
        <h1 className="font-editorial text-5xl mb-4">Story not found.</h1>
        <p className="text-[#A1A1AA]">This project might be private, unpublished, or the link has changed.</p>
        <Link to="/community" className="mt-8 inline-block border border-[#27272A] hover:border-[#A1A1AA] px-6 py-3 text-sm">Browse community</Link>
      </div>
    </MarketingLayout>
  );
}
